import { batch } from 'react-redux';
import { createSelector, Dictionary } from '@reduxjs/toolkit';
import { selectResources } from '@ff-os/pdp-shared-redux/slices/resourcesSlice';
import { selectTenantId } from '@ff-os/pdp-shared-redux/slices/tenantSlice';
import { applyPalette } from '@ff-os/rendering-shared/coloring';
import { toList } from '@ff-os/shared-utils/dictionaries';
import { Translate } from '@ff-os/shared-utils/types/Translate';
import { unwrap } from '@ff-os/shared-utils/unwrap';
import { createUserInputsPatch } from '@ff-resource-plugins/prefabs-shared/merge-logic/createUserInputsPatch';
import { Prefab } from '@ff-resource-plugins/prefabs-shared/Prefab';
import { selectActiveVariant, selectActiveVariantId } from '@ff-scene-plugins/gift-redux/selectors/variantSelectors';
import { actions as inputActions } from '@ff-scene-plugins/gift-redux/slices/inputsSlice';
import { actions as variableActions, selectPinnedVariables } from '@ff-scene-plugins/gift-redux/slices/variablesSlice';
import { GiftPdpState, GiftPdpThunk } from '@ff-scene-plugins/gift-redux/store';
import { GiftJson } from '@ff-scene-plugins/gift-shared/GiftJson';
import { selectActivePrefabId, updateCommission, updatePrefabId, updateViewboxHeight, updateViewboxWidth } from './_redux-meta';
import { createPrefabPreviewPaintables } from './preview/helpers/createPrefabPreviewPaintables';

export const selectPrefabs = selectResources<Prefab>('prefab');

export const filterPrefabs = (
    prefabs: Dictionary<Prefab>,
    tenantId: string,
    variantId: string,
    activePrefabId?: string
) => {
    return toList(prefabs)
        .filter(p => !p.excludedFromVariants?.includes(variantId) || p.id === activePrefabId)
        .filter(p => !p.exclusiveTo?.length || p.exclusiveTo.includes(tenantId));
};

export const selectFilteredPrefabs = createSelector([
    selectPrefabs,
    selectTenantId,
    selectActiveVariantId,
    selectActivePrefabId
], filterPrefabs);

export const selectHasMultiplePrefabs = createSelector(
    selectFilteredPrefabs,
    prefabs => prefabs.length > 1);

export const selectActivePrefab = createSelector([
    selectPrefabs,
    selectActivePrefabId
], (prefabs, id) => toList(prefabs).find(p => p.id === id));

export const selectIsActivePrefab = createSelector([
    selectActivePrefabId,
    (state: GiftPdpState, prefabId: string) => prefabId
], (activePrefabId, prefabId) => activePrefabId === prefabId);

export const applyPrefab = (prefab: Prefab): GiftPdpThunk => {

    return (dispatch, getState) => {

        const state = getState();

        const newVariables = unwrap(prefab.variables);
        const allVariables = newVariables.concat(selectPinnedVariables(state));

        const patchedInputs = createUserInputsPatch(state.project.inputs, prefab);

        // Set inputs BEFORE variables: we always do `variables.map(v => inputs[v.id])`,
        // so adding a variable before its corresponding input may cause errors.
        //
        // (The `batch(...)` wrapper ensures React components aren't notified until both
        // dispatches finish, but it does not apply to store subscribers.)

        batch(() => {
            dispatch(inputActions.upsertMany(toList(patchedInputs)));
            dispatch(variableActions.setAll(allVariables));
            dispatch(updateCommission(prefab.commission?.id));
            dispatch(updatePrefabId(prefab.id));
            dispatch(updateViewboxWidth(prefab.viewbox.width));
            dispatch(updateViewboxHeight(prefab.viewbox.height));
        });
    };
};

export const selectCommissionAssetId = createSelector([
    selectActivePrefab
], prefab => prefab && prefab.commission?.logoAssetId);

export const selectCommissionName = createSelector([
    selectActivePrefab
], prefab => prefab && prefab.commission?.name);

const selectPrefabById = createSelector([
    selectPrefabs,
    (state: GiftPdpState, id: string) => id
], (prefabs, id) => prefabs[id]!);

export const selectPrefabViewboxById = createSelector([
    (state: GiftPdpState, id: string) => selectPrefabById(state, id)
], prefab => prefab.viewbox);

export const selectPaintablesForPrefabPreviewById = createSelector([
    selectPrefabById,
    (s: GiftPdpState) => s.project.inputs,
    (s: GiftPdpState) => selectActiveVariant(s),
    (s: GiftPdpState, prefabId: string, t: Translate) => t,
    (s: GiftPdpState) => s.auth.jwt
], createPrefabPreviewPaintables);

export const selectPreviewBackgroundColor = createSelector([
    (state: GiftPdpState, prefabId: string) => selectPrefabById(state, prefabId),
    (state: GiftPdpState, prefabId: string) => selectActiveVariant(state)
], (prefab: Prefab, variant: GiftJson.Variant<any>) => {

    if (prefab.previewBackgroundColor) {
        return applyPalette(prefab.previewBackgroundColor, variant.palette);
    } else {
        return undefined;
    }
});